import React, { useState, useEffect } from 'react';
import { Rocket, Loader2, AlertCircle } from 'lucide-react';

/**
 * RegisterView - User registration component
 * Collects name, email, and specialization
 */
function RegisterView({ onRegister, loading, specializations, onLoadSpecs }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [specId, setSpecId] = useState('nursing');
  const [specs, setSpecs] = useState(specializations || []);
  const [error, setError] = useState('');

  useEffect(() => {
    if (specs.length === 0 && onLoadSpecs) {
      onLoadSpecs().then(data => setSpecs(data || [])).catch(() => {});
    }
  }, [specs.length, onLoadSpecs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (name.trim()) {
      try {
        await onRegister(name.trim(), email.trim() || `${name.toLowerCase().replace(/\s/g, '')}@mydoctor.app`, specId);
      } catch (err) {
        setError('שגיאה בחיבור לשרת. נסה שוב בעוד רגע.');
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 p-6" dir="rtl">
      <div className="bg-white p-10 sm:p-16 rounded-[3rem] shadow-2xl max-w-xl w-full text-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-2 bg-teal-500"></div>
        
        <h2 className="text-4xl sm:text-5xl font-black mb-4 text-slate-900" data-testid="register-title">
          ברוכים הבאים 👋
        </h2>
        <p className="text-lg text-slate-400 font-medium mb-10">
          נשאר רק להכיר אותך, ונוכל לצאת לדרך
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2 text-sm" data-testid="register-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 text-right">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 block pr-2">שם מלא</label>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              className="input-primary"
              placeholder="ישראל ישראלי"
              required
              data-testid="register-name-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 block pr-2">אימייל (אופציונלי)</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-primary"
              placeholder="email@example.com"
              data-testid="register-email-input"
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-500 block pr-2">מסלול לימודים</label>
            <select
              value={specId}
              onChange={(e) => setSpecId(e.target.value)}
              className="input-primary appearance-none cursor-pointer"
              data-testid="register-spec-select"
            >
              {specs
                .filter(spec => spec.id !== 'gov-exams')
                .map(spec => (
                  <option key={spec.id} value={spec.id}>{spec.name}</option>
                ))}
              {specs.length === 0 && <option value="nursing">סיעוד</option>}
            </select>
          </div>

          <button
            type="submit"
            disabled={!name.trim() || loading}
            className="w-full bg-slate-900 hover:bg-black text-white py-5 rounded-2xl font-black text-xl mt-8 active:scale-95 disabled:opacity-30 transition-all flex items-center justify-center gap-3"
            data-testid="register-submit-button"
          >
            {loading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <span>המשך למערכת</span>
                <Rocket className="w-5 h-5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegisterView;
